<script lang="ts">
	import { page } from "$app/state";
	import {
		LayoutDashboard,
		Languages,
		Settings,
		HelpCircle,
		LogOut,
		School,
		Shield,
		Users,
	} from "@lucide/svelte";
	import { userStore } from "$lib/stores/user";
	import { t } from "svelte-i18n";

	const role = $derived($userStore.user?.role ?? "user");

	const adminLinks = [
		{ href: "/admin", labelKey: "Admin Overview", icon: Shield },
		{ href: "/admin/users", labelKey: "Users", icon: Users },
		{ href: "/scripts", labelKey: "shared.scripts", icon: Languages },
	];

	const learningLinks = [
		{
			href: "/dashboard",
			labelKey: "shared.dashboard",
			icon: LayoutDashboard,
		},
		{ href: "/scripts", labelKey: "shared.scripts", icon: Languages },
	];

	// Students only see dashboard, not scripts browser
	const studentLearningLinks = [
		{
			href: "/dashboard",
			labelKey: "shared.dashboard",
			icon: LayoutDashboard,
		},
	];

	const accountLinks = [
		{
			href: "/settings",
			openInNewTab: false,
			labelKey: "nav.settings",
			icon: Settings,
		},
		{
			href: "https://coda.io/d/Grafem_doNfF_A_A0f",
			openInNewTab: true,
			labelKey: "nav.help",
			icon: HelpCircle,
		},
	];

	const activeLearningLinks = $derived(
		role === "student" ? studentLearningLinks : learningLinks,
	);

	function isActive(href: string): boolean {
		const path = page.url.pathname;
		if (href === "/dashboard") return path === "/dashboard";
		if (href === "/scripts")
			return path === "/scripts" || path.startsWith("/scripts/");
		if (href === "/admin")
			return path === "/admin" && !path.startsWith("/admin/");
		if (href === "/admin/users") return path.startsWith("/admin/users");
		return page.url.pathname === href;
	}

	async function handleLogout() {
		userStore.logout();
		window.location.href = "/auth/login";
	}
</script>

<aside
	class="hidden md:flex w-[256px] shrink-0 flex-col rounded-r-(--radius-m) border-r border-(--sidebar-border) bg-(--sidebar) shadow-(--shadow-sidebar) min-h-screen rtl:rounded-r-none rtl:rounded-l-(--radius-m) rtl:border-r-0 rtl:border-l"
>
	<div class="flex items-center justify-between px-6 py-6">
		<a
			href="/"
			class="text-[22px] font-bold text-(--foreground) no-underline"
		>
			grafem
		</a>
	</div>

	<nav class="flex flex-1 flex-col gap-6 px-4 pt-2">
		{#if role === "admin"}
			<div>
				<p
					class="mb-2 p-2 text-[14px] font-semibold tracking-[1.5px] uppercase text-(--sidebar-foreground)"
				>
					Admin
				</p>
				<ul class="flex flex-col gap-0.5">
					{#each adminLinks as link (link.href)}
						<li>
							<a
								href={link.href}
								class="flex items-center gap-3 rounded-(--radius-m) py-3 pl-6 pr-4 text-[14px] font-medium no-underline transition-colors
									{isActive(link.href)
									? 'bg-(--sidebar-accent) text-(--sidebar-accent-foreground)'
									: 'text-(--sidebar-foreground) hover:bg-(--sidebar-accent) hover:text-(--sidebar-accent-foreground)'}"
							>
								<link.icon size={18} strokeWidth={1.5} />
								{link.labelKey.startsWith("shared.")
									? $t(link.labelKey)
									: link.labelKey}
							</a>
						</li>
					{/each}
				</ul>
			</div>
		{:else}
			<div>
				<p
					class="mb-2 p-2 text-[14px] font-semibold tracking-[1.5px] uppercase text-(--sidebar-foreground)"
				>
					{$t("shared.learning")}
				</p>
				<ul class="flex flex-col gap-0.5">
					{#each activeLearningLinks as link (link.labelKey)}
						<li>
							<a
								href={link.href}
								class="flex items-center gap-3 rounded-(--radius-m) py-3 pl-6 pr-4 text-[14px] font-medium no-underline transition-colors
									{isActive(link.href)
									? 'bg-(--sidebar-accent) text-(--sidebar-accent-foreground)'
									: 'text-(--sidebar-foreground) hover:bg-(--sidebar-accent) hover:text-(--sidebar-accent-foreground)'}"
							>
								<link.icon size={18} strokeWidth={1.5} />
								{$t(link.labelKey)}
							</a>
						</li>
					{/each}
				</ul>
			</div>
		{/if}

		<div>
			<p
				class="mb-2 p-2 text-[14px] font-semibold tracking-[1.5px] uppercase text-(--sidebar-foreground)"
			>
				{$t("nav.account")}
			</p>
			<ul class="flex flex-col gap-0.5">
				{#each accountLinks as link (link.labelKey)}
					<li>
						<a
							href={link.href}
							target={link.openInNewTab ? "_blank" : ""}
							class="flex items-center gap-3 rounded-(--radius-m) py-3 pl-6 pr-4 text-[14px] font-medium no-underline transition-colors
								{isActive(link.href)
								? 'bg-(--sidebar-accent) text-(--sidebar-accent-foreground)'
								: 'text-(--sidebar-foreground) hover:bg-(--sidebar-accent) hover:text-(--sidebar-accent-foreground)'}"
						>
							<link.icon size={18} strokeWidth={1.5} />
							{$t(link.labelKey)}
						</a>
					</li>
				{/each}
				<li>
					<button
						onclick={handleLogout}
						class="w-full flex items-center gap-3 rounded-(--radius-m) py-3 pl-6 pr-4 text-[14px] font-medium transition-colors border-0 bg-transparent cursor-pointer text-(--sidebar-foreground) hover:bg-(--sidebar-accent) hover:text-(--sidebar-accent-foreground)"
					>
						<LogOut size={18} strokeWidth={1.5} />
						{$t("nav.logout")}
					</button>
				</li>
			</ul>
		</div>
	</nav>
</aside>
